import { useState, useEffect, ReactElement } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Typography,
  Box,
  FormControl,
  OutlinedInput,
  Button,
  IconButton,
  DialogTitle,
  DialogContentText,
  DialogActions,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import * as thaiIdCard from 'thai-id-card';

import { DialogContentStyle, FormHelperTextStyle, useCssClasses } from './style';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import MaskedCitizenIdInput from '@components/atoms/input/CitizenIdInput';

type ValidationResult = { isError: boolean; message: string };

const initialValidationResult: ValidationResult = {
  isError: false,
  message: '',
};

const citizenIdCharacterLength = 13;

type Props = {
  dialog: { opened: boolean; open: () => void; close: () => void; render: (children: ReactElement) => JSX.Element };
  onSubmit: (value: string) => void;
};

const CitizenIdModal = ({ dialog, onSubmit }: Props) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [citizenId, setCitizenId] = useState<string>('');
  const [isValidCitizenId, setIsValidCitizenId] = useState<boolean>(false);
  const [citizenIdValidationResult, setCitizenIdValidationResult] = useState<ValidationResult>(initialValidationResult);
  const { t } = useTranslation();
  const classes = useCssClasses();

  const validateCitizenId = (value: string) => {
    if (!value) {
      return {
        isError: true,
        message: t('cpd_course.fill_citizen_id_placeholder'),
      };
    }

    if (!thaiIdCard.verify(value)) {
      return {
        isError: true,
        message: t('cpd_course.invalid_citizen_id_format'),
      };
    }

    return {
      ...initialValidationResult,
    };
  };

  const clear = () => {
    setCitizenId('');
    setCitizenIdValidationResult(initialValidationResult);
  };

  const handleCitizenIdInput = ({ target }: any) => {
    const { value } = target;

    const validationResult = validateCitizenId(value);
    setCitizenIdValidationResult(validationResult);
    setCitizenId(value);
    setIsValidCitizenId(value.length === citizenIdCharacterLength && !validationResult.isError);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const validationResult = validateCitizenId(citizenId);

    if (validationResult.isError) {
      setCitizenIdValidationResult(validationResult);
      return;
    }

    await onSubmit(citizenId);
    dialog.close();
    clear();
    setLoading(false);
  };

  useEffect(
    () => () => {
      clear();
    },
    [dialog.opened],
  );

  return (
    <>
      <DialogTitle>
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton onClick={dialog.close} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box display="flex" alignItems="center" flexDirection="column" rowGap="1">
          <FontAwesomeIcon
            style={{ fontSize: '50px' }}
            color={theme.palette.grey[500]}
            icon="fa-light fa-circle-exclamation"
          />
          <Typography color="primary.main" variant="h3" align="center" sx={{ mt: 2 }}>
            {t('cpd_course.check_info_purchasing_course')}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContentStyle>
        <DialogContentText>
          <Typography sx={{ fontFamily: 'Lanna' }} align="center" variant="body2_deprecated">
            <span style={{ color: '#F44336' }}>*</span>
            {t('cpd_course.please_fill_citizen_id')}
          </Typography>
        </DialogContentText>
        <Box display="flex" justifyContent="center" sx={{ mt: 1 }}>
          <FormControl className={classes.input}>
            <OutlinedInput
              placeholder={t('cpd_course.fill_citizen_id_placeholder')}
              size="small"
              value={citizenId}
              error={citizenIdValidationResult.isError}
              onChange={handleCitizenIdInput}
              inputComponent={MaskedCitizenIdInput as any}
              disabled={false}
            />
            {citizenIdValidationResult.isError && (
              <FormHelperTextStyle>{citizenIdValidationResult.message}</FormHelperTextStyle>
            )}
          </FormControl>
        </Box>
      </DialogContentStyle>
      <DialogActions sx={{ justifyContent: 'center', pr: '24px', pl: '24px' }}>
        <Button
          disabled={!isValidCitizenId || loading}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          className={classes.button}
        >
          {loading ? <CircularProgress size={24} /> : t('cpd_course.examine')}
        </Button>
      </DialogActions>
    </>
  );
};

export default CitizenIdModal;
